import type { Ticket } from '@/interface/ITicket';
import { TicketType } from '@/interface/enum';

export interface IParlayTicketOrderState {
    lastAssignedOrder: number;
    ticketOrderByKey: Record<string, number>;
    eventGroupOrderById: Record<number, number>;
}

export interface IParlayTicketGroup {
    key: string;
    eventId: number | null;
    tickets: Ticket[];
}

export function createParlayTicketOrderState(): IParlayTicketOrderState {
    return {
        lastAssignedOrder: 0,
        ticketOrderByKey: {},
        eventGroupOrderById: {},
    };
}

export function syncParlayTicketOrder(
    state: IParlayTicketOrderState,
    tickets: Ticket[],
): IParlayTicketOrderState {
    let lastAssignedOrder = state.lastAssignedOrder;
    const ticketOrderByKey: Record<string, number> = {};
    tickets.forEach((ticket) => {
        const existingOrder = state.ticketOrderByKey[ticket.key];
        if (existingOrder === undefined) {
            lastAssignedOrder += 1;
            ticketOrderByKey[ticket.key] = lastAssignedOrder;
            return;
        }
        ticketOrderByKey[ticket.key] = existingOrder;
    });
    const eventGroupOrderById: Record<number, number> = {};
    tickets.forEach((ticket) => {
        if (ticket.type !== TicketType.Single) {
            return;
        }
        const existingGroupOrder = eventGroupOrderById[ticket.eventId]
            ?? state.eventGroupOrderById[ticket.eventId]
            ?? 0;
        eventGroupOrderById[ticket.eventId] = Math.max(
            existingGroupOrder,
            ticketOrderByKey[ticket.key],
        );
    });
    return {
        lastAssignedOrder,
        ticketOrderByKey,
        eventGroupOrderById,
    };
}

export function sortTicketsByParlayOrder(
    state: IParlayTicketOrderState,
    tickets: Ticket[],
): Ticket[] {
    return [...tickets].sort((left, right) => {
        const groupOrderDifference = getTicketGroupOrder(state, left)
            - getTicketGroupOrder(state, right);
        if (groupOrderDifference !== 0) {
            return groupOrderDifference;
        }
        return (state.ticketOrderByKey[left.key] ?? 0)
            - (state.ticketOrderByKey[right.key] ?? 0);
    });
}

export function groupTicketsByParlayOrder(
    state: IParlayTicketOrderState,
    tickets: Ticket[],
): IParlayTicketGroup[] {
    const groupsByKey = new Map<string, IParlayTicketGroup>();
    sortTicketsByParlayOrder(state, tickets).forEach((ticket) => {
        const eventId = ticket.type === TicketType.Single ? ticket.eventId : null;
        const groupKey = eventId === null ? `ticket:${ticket.key}` : `event:${eventId}`;
        const group = groupsByKey.get(groupKey);
        if (group) {
            group.tickets.push(ticket);
            return;
        }
        groupsByKey.set(groupKey, {
            key: groupKey,
            eventId,
            tickets: [ticket],
        });
    });
    return [...groupsByKey.values()];
}

function getTicketGroupOrder(state: IParlayTicketOrderState, ticket: Ticket): number {
    return ticket.type === TicketType.Single
        ? state.eventGroupOrderById[ticket.eventId] ?? 0
        : state.ticketOrderByKey[ticket.key] ?? 0;
}
